import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, LOGO_URL } from "@/lib/api";
import { money0, sessionId, PARTNER_IMG } from "@/lib/partners";
import { LeadForm } from "@/components/client/LeadForm";
import { ArrowDown, ArrowRight, Check } from "lucide-react";

const Kicker = ({ children, light }) => (
  <p className={`text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold ${light ? "text-[#c19a6b]" : "text-[#c19a6b]"}`}>{children}</p>
);

const Step = ({ img, title, body }) => (
  <div className="flex-1">
    <img src={img} alt={title} className="w-full aspect-[4/3] object-cover" loading="lazy" />
    <p className="text-[10px] uppercase tracking-[0.28em] text-[#c19a6b] mt-6">{title}</p>
    <p className="text-[15px] text-gray-600 leading-relaxed mt-3">{body}</p>
  </div>
);

export default function ClientRevival() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [d, setD] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/revival/${slug}`, { params: { sid: sessionId(), preview: preview ? 1 : 0 } })
      .then((r) => setD(r.data))
      .catch(() => setError(true));
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
  }, [slug, preview]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-12 mb-8" />
        <h1 className="font-display text-2xl mb-3">This client page isn't available</h1>
        <p className="text-gray-500 text-sm">Please contact ARK Furniture or your realtor for an updated link.</p>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-[#c19a6b] rounded-full animate-spin" />
      </div>
    );
  }

  const r = d.realtor;
  const headline =
    d.headline_style === "B"
      ? `${r.first_name} takes care of his clients. So we do too.`
      : `Any friend of ${r.display_name}'s is a friend of ours.`;
  const topSaving = d.examples.reduce((a, e) => Math.max(a, e.savings_cents), 0);
  const scrollToForm = () => document.getElementById("request")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between gap-4">
          <img src={LOGO_URL} alt="ARK Furniture" className="h-10 sm:h-11" />
          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-[#c19a6b] border border-[#c19a6b]/40 px-3 py-1.5 text-right">
            {d.program_name}
          </span>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-16 text-center fade-up">
        <Kicker>A Private Client Benefit From</Kicker>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-6" data-testid="client-realtor-name">
          {r.display_name}
        </h1>
        {(r.brokerage || r.city) && (
          <p className="text-[11px] uppercase tracking-[0.22em] text-gray-400 mt-4">
            {[r.brokerage, r.city].filter(Boolean).join(" · ")}
          </p>
        )}
        {r.headshot_url && (
          <img src={r.headshot_url} alt={r.display_name} className="w-20 h-20 rounded-full object-cover mx-auto mt-8 ring-1 ring-[#c19a6b]/50" />
        )}

        <h2 className="font-display text-2xl sm:text-3xl mt-14 max-w-2xl mx-auto leading-snug" data-testid="client-headline">
          {headline}
        </h2>
        <p className="text-gray-600 mt-6 max-w-xl mx-auto leading-relaxed">
          As a client of {r.display_name}, you're receiving exclusive access to ARK's Signature Revival at our
          Essential Revival rate.
        </p>

        <div className="max-w-md mx-auto mt-14 border border-[#c19a6b]/40 p-8" data-testid="benefit-box">
          <p className="text-[10px] uppercase tracking-[0.24em] text-gray-400">Your benefit</p>
          <p className="font-display text-3xl mt-3">Signature Revival</p>
          <div className="flex items-center justify-center gap-6 mt-7">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Regular rate</p>
              <p className="text-lg text-gray-400 line-through decoration-[#c19a6b]/70 mt-1">Signature price</p>
            </div>
            <ArrowRight size={16} className="text-[#c19a6b]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8a6a42]">Your {r.first_name} client rate</p>
              <p className="font-display text-xl text-[#c19a6b] mt-1">Essential price</p>
            </div>
          </div>
          {topSaving > 0 && (
            <p className="text-sm text-gray-500 mt-7">Savings of up to {money0(topSaving)} per piece.</p>
          )}
        </div>

        <button
          data-testid="start-quote-btn"
          onClick={scrollToForm}
          className="mt-12 h-14 px-10 bg-black text-white text-[11px] uppercase tracking-[0.22em] font-bold btn-lift inline-flex items-center gap-2"
        >
          Start My Revival Quote <ArrowRight size={15} />
        </button>
      </section>

      {/* PRICING EXAMPLES */}
      <section className="bg-[#0B0B0B] text-white py-20 sm:py-28" data-testid="client-pricing">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <Kicker>Your private client rates</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-5">The Signature service, at the Essential rate.</h2>
          </div>
          <div className="mt-14 border-t border-white/10">
            {d.examples.map((e) => (
              <div key={e.key} className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr] gap-x-4 gap-y-1 py-5 border-b border-white/10 items-baseline">
                <span className="font-display text-xl col-span-2 sm:col-span-1">{e.label}</span>
                <span className="text-white/35 sm:text-right line-through decoration-[#c19a6b]/70" data-testid={`client-retail-${e.key}`}>
                  {money0(e.retail_cents)}
                </span>
                <span className="font-display text-xl text-[#c19a6b] sm:text-right" data-testid={`client-rate-${e.key}`}>
                  {money0(e.client_cents)}
                </span>
              </div>
            ))}
          </div>
          <p className="text-center text-white/45 text-sm mt-8 leading-relaxed">
            Final pricing depends on your specific pieces — your personalized quote will show your exact private
            client savings.
          </p>
        </div>
      </section>

      {/* MOVING */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <Kicker>Moving?</Kicker>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-5 leading-tight">
            Let us take the furniture off your moving list.
          </h2>
        </div>
        <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-6 mt-14">
          <Step img={PARTNER_IMG.oldHome} title="Your Old Home" body="ARK picks up the furniture before or during your move." />
          <div className="flex items-center justify-center lg:pt-24"><ArrowDown className="text-[#c19a6b] lg:-rotate-90" size={22} /></div>
          <Step img={PARTNER_IMG.workshop} title="ARK Workshop" body="We revive it while you move and settle in." />
          <div className="flex items-center justify-center lg:pt-24"><ArrowDown className="text-[#c19a6b] lg:-rotate-90" size={22} /></div>
          <Step img={PARTNER_IMG.newHome} title="Your New Home" body="We deliver it refreshed and ready for your new space." />
        </div>
        <div className="grid sm:grid-cols-2 gap-x-14 mt-16">
          {[
            "One less thing for movers to handle",
            "Reduced risk of furniture damage during the move",
            "No need to move pieces twice",
            "Refresh quality furniture instead of replacing it",
            "Settle into the new home with finished pieces",
          ].map((b) => (
            <div key={b} className="flex items-start gap-3 py-4 border-b border-gray-100">
              <Check size={15} className="text-[#c19a6b] shrink-0 mt-1" />
              <span className="text-[15px] text-gray-700 leading-snug">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-[#FAF7F2] py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <Kicker>Included in your Signature Revival</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-5">Our highest level of care.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-12 mt-14">
            {d.signature_includes.map((inc) => (
              <div key={inc} className="flex items-start gap-3 py-4 border-b border-black/5">
                <Check size={15} className="text-[#c19a6b] shrink-0 mt-1" />
                <span className="text-sm text-gray-700 leading-snug">{inc}</span>
              </div>
            ))}
          </div>
          <div className="bg-white border-l-2 border-[#c19a6b] p-8 mt-14">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#c19a6b] font-bold">What is resurfacing?</p>
            <p className="text-gray-700 leading-relaxed text-[15px] mt-4">{d.resurfacing_explanation}</p>
            <p className="text-gray-500 leading-relaxed text-sm mt-4 italic">{d.resurfacing_note}</p>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="client-gallery">
          <div className="text-center">
            <Kicker>Real ARK Revivals</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-5">Furniture we've brought back to life.</h2>
          </div>
          <div className={`grid gap-8 mt-14 ${gallery.length === 1 ? "max-w-xl mx-auto" : "sm:grid-cols-2"}`}>
            {gallery.slice(0, 4).map((g) => (
              <div key={g.id} className="bg-white border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="grid grid-cols-2 gap-1 p-1">
                  {[["Before", g.before_photo_id], ["After", g.after_photo_id]].map(([lab, pid]) => (
                    <div key={lab} className="relative">
                      <img src={`${API}/photos/${pid}`} alt={`${g.title} — ${lab}`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                      <span className={`absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 ${lab === "After" ? "bg-[#c19a6b] text-white" : "bg-black/70 text-white"}`}>
                        {lab}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="font-display text-lg text-center py-4">{g.title}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* REQUEST FORM */}
      <section id="request" className="border-t border-gray-100 py-20 sm:py-28 scroll-mt-4">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Kicker>Start your revival</Kicker>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-5 leading-tight">
              Tell us about your pieces.
            </h2>
            <p className="text-gray-500 mt-6 leading-relaxed">
              ARK will review your furniture and send your personalized quote with your {r.display_name} client rate
              already applied.
            </p>
          </div>
          <LeadForm slug={slug} realtorName={r.display_name} />
        </div>
      </section>

      {/* PROMISE */}
      <section className="bg-[#0B0B0B] text-white py-20 sm:py-24">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <Kicker>The ARK Resurfacing Promise</Kicker>
          <p className="text-white/65 leading-relaxed mt-8">{d.ark_promise}</p>
        </div>
      </section>

      <footer className="py-12 text-center">
        <img src={LOGO_URL} alt="ARK Furniture" className="h-9 mx-auto mb-4 opacity-80" />
        <p className="text-[11px] text-gray-400 tracking-wide">
          {d.program_name} · A private client benefit from {r.display_name}
        </p>
      </footer>
    </div>
  );
}
